// ./client/src/utilities/validations/notebokkCodeValidation.js

const AI_PIPELINE_ORDER = [
  'data', 'preprocessing', 'model', 'training', 'evaluation', 'output'
];

// ── Pin input validation ───────────────────────────────────────────────────
// Called in NotebookCell before run.
// Checks that every bare variable used in currentCode was assigned in a
// previous cell, a Python builtin, or a common import alias.
export const validatePinInputs = (currentCode, allCellCodes, currentIndex) => {
  const errors = [];

  // Variables always available in Pyodide environment
  const definedVars = new Set([
    'pd', 'np', 'plt', 'sns', 'tf', 'torch', 'nn',
    'print', 'len', 'range', 'list', 'dict', 'set', 'tuple',
    'str', 'int', 'float', 'bool', 'True', 'False', 'None',
    'open', 'zip', 'enumerate', 'type', 'isinstance', 'hasattr',
    'getattr', 'setattr', 'max', 'min', 'sum', 'abs', 'round',
    'sorted', 'reversed', 'map', 'filter', 'any', 'all',
    'ValueError', 'TypeError', 'KeyError', 'IndexError', 'Exception',
  ]);

  // Also scan current cell's own code for assignments
  const currentCellLines = currentCode.split('\n');
  currentCellLines.forEach(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith('#')) return;
    const simpleAssign = trimmed.match(/^([a-zA-Z_]\w*)\s*=/);
    if (simpleAssign) definedVars.add(simpleAssign[1]);
    const multiAssign = trimmed.match(/^([a-zA-Z_]\w*(?:\s*,\s*[a-zA-Z_]\w*)+)\s*=/);
    if (multiAssign) multiAssign[1].split(',').forEach(v => definedVars.add(v.trim()));
  });

  // Scan ALL previous cells for assigned variable names
  for (let i = 0; i < currentIndex; i++) {
    const lines = (allCellCodes[i] || '').split('\n');
    lines.forEach(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('#')) return;

      // Simple assignment: varName = ...
      const simpleAssign = trimmed.match(/^([a-zA-Z_]\w*)\s*=/);
      if (simpleAssign) definedVars.add(simpleAssign[1]);

      // Tuple/multi-assign: a, b = ...
      const multiAssign = trimmed.match(/^([a-zA-Z_]\w*(?:\s*,\s*[a-zA-Z_]\w*)+)\s*=/);
      if (multiAssign) {
        multiAssign[1].split(',').forEach(v => definedVars.add(v.trim()));
      }

      // For loop variable: for x in ...
      const forLoop = trimmed.match(/^for\s+([a-zA-Z_]\w*)\s+in\b/);
      if (forLoop) definedVars.add(forLoop[1]);

      // Import alias: import X as Y / from X import Y
      const importAs = trimmed.match(/import\s+\w+\s+as\s+([a-zA-Z_]\w*)/);
      if (importAs) definedVars.add(importAs[1]);

      const fromImport = trimmed.match(/from\s+\S+\s+import\s+([a-zA-Z_]\w*(?:\s*,\s*[a-zA-Z_]\w*)*)/);
      if (fromImport) {
        fromImport[1].split(',').forEach(v => definedVars.add(v.trim()));
      }
    });
  }

  // Check current cell body lines for undefined bare variable usage
  const bodyLines = currentCode.split('\n').filter(l => {
    const t = l.trim();
    return (
      t.length > 0 &&
      !t.startsWith('#') &&
      !t.startsWith('import') &&
      !t.startsWith('from') &&
      !t.startsWith('def ') &&
      !t.startsWith('class ')
    );
  });

  // In validatePinInputs, replace the bodyLines.forEach block:
  bodyLines.forEach(line => {
    const trimmed = line.trim();

    // Skip assignment lines
    if (trimmed.match(/^[a-zA-Z_]\w*\s*=/) ||
      trimmed.match(/^[a-zA-Z_]\w*\s*\[/)) return;

    // Find bare variable references — only the FIRST token before a dot, bracket, or operator
    // e.g. "df.select_dtypes" → only check "df"
    // e.g. "np.random.normal" → only check "np"
    // e.g. "df_augmented[numeric_cols]" → only check "df_augmented"
    const firstToken = trimmed.match(/^([a-zA-Z_]\w*)/);
    if (firstToken) {
      const varName = firstToken[1];
      if (!definedVars.has(varName)) {
        // Skip Python keywords
        const keywords = new Set(['if', 'else', 'elif', 'for', 'while', 'try',
          'except', 'with', 'return', 'yield', 'class', 'def', 'pass',
          'break', 'continue', 'raise', 'assert', 'del', 'lambda',
          'print', 'True', 'False', 'None']);
        if (!keywords.has(varName)) {
          errors.push(
            `⚠️ Variable "${varName}" may not be defined — ensure previous cells have been run`
          );
        }
      }
    }
  });

  // Deduplicate errors
  return [...new Set(errors)];
};

// ── Pipeline order validation ──────────────────────────────────────────────
// Checks that components follow the correct category order in the pipeline.
export const validatePipelineOrder = (components) => {
  const errors = [];
  const warnings = [];

  let lastOrderIndex = -1;

  components.forEach((comp, i) => {
    const orderIndex = AI_PIPELINE_ORDER.indexOf(comp.category);
    if (orderIndex === -1) return; // Unknown category — skip

    if (orderIndex < lastOrderIndex) {
      warnings.push(
        `"${comp.label || comp.componentName}" (${comp.category}) appears after a later-stage component`
      );
    }
    lastOrderIndex = Math.max(lastOrderIndex, orderIndex);
  });

  return { errors, warnings };
};

export default {validatePinInputs, validatePipelineOrder};